<template>
  <div>
    <ask
      ref="confirmMove"
      title="Move Payment?"
      message="Are you sure you would like to move this payment?"
      @ok="move"
    />

    <ask
      ref="processPaymentNowModal"
      title="Process Payment"
      :message="
        `Are you sure you would like to process this payment ahead of the scheduled date?`
      "
      @ok="processPaymentNow"
    />

    <table class="table">
      <thead>
        <tr>
          <th style="width:2em">&nbsp;</th>
          <th>Cycle</th>
          <th>Date</th>
          <th style="width:120px" class="text-right">
            Amount
          </th>
          <th class="text-right pr-0">
            Action
          </th>
        </tr>
      </thead>
      <template v-for="(item, index) in myPaymentPlanDetails">
        <tr v-bind:key="item.id" v-if="item.isEdit">
          <td class="align-middle">
            <i
              style="font-size: 2em"
              class="icomoon icon-status-approved text-success"
              v-if="item.processedToLedger"
            ></i>
            <i
              style="font-size: 2em;color:#efefef"
              class="icomoon icon-status-approved"
              v-if="!item.processedToLedger"
            ></i>
          </td>
          <td class="align-middle">
            <template v-if="item.isDownPayment">
              <b>Down Payment</b>
            </template>
            <template v-else>
              {{ item.cycle }}
            </template>
          </td>
          <td colspan="4">
            <div class="row">
              <div class="col">
                <b-form-datepicker
                  v-model="item.billingDate"
                  :min="
                    index - 1 <= 0
                      ? new Date(Date.now() + 3600 * 1000 * 24)
                      : myPaymentPlanDetails[index - 1].billingDate
                  "
                  :max="
                    index + 1 == myPaymentPlanDetails.length
                      ? null
                      : myPaymentPlanDetails[index + 1].billingDate
                  "
                ></b-form-datepicker>

                <b-checkbox class="mt-4" v-model="item.changeChildren">
                  <template v-if="paymentPlan.cycle == 'MONTHLY'">
                    Update remaining cycles to run on the
                    {{ item.billingDate.split("-")[2] | ordinal }} of each
                    month.
                  </template>
                  <template v-else>
                    Update remaining cycles to run
                    {{ paymentPlan.cycle | lowercase }}ly on the newly selected
                    date.
                  </template>
                </b-checkbox>
              </div>
              <div class="col">
                <b-row class="row" align-h="end">
                  <b-col cols="12" style="text-align:right">
                    <div class="input-group-sm">
                      <validation-observer
                        :ref="'formValidator' + item.cycle"
                        v-slot="{ handleSubmit }"
                      >
                        <form
                          method="post"
                          @submit.prevent="handleSubmit(save)"
                        >
                          <base-input-currency
                            :inputId="'amountInput' + item.cycle"
                            prependChar="$"
                            setFocus="true"
                            name="Cycle Amount"
                            rules="moneyZeroOk"
                            v-model="item.amountDueConverted"
                          >
                            <template #appendButton>
                              <button class="btn btn-primary" type="submit">
                                <i class="fas fa-save"></i>
                              </button>
                            </template>
                          </base-input-currency>
                        </form>
                      </validation-observer>
                    </div>
                  </b-col>
                </b-row>
              </div>
            </div>
          </td>
        </tr>
        <tr v-bind:key="item.id" v-if="!item.isEdit">
          <td class="align-middle">
            <i
              style="font-size: 2em"
              class="icomoon icon-status-approved text-success"
              v-if="item.processedToLedger"
            ></i>
            <i
              style="font-size: 2em;color:#efefef"
              class="icomoon icon-status-approved"
              v-if="!item.processedToLedger"
            ></i>
          </td>
          <td class="align-middle">
            <template v-if="item.isDownPayment">
              <b>Down Payment</b>
            </template>
            <template v-else>
              {{ item.cycle }}
            </template>
          </td>
          <td class="align-middle">
            {{ item.billingDate | moment("MM/DD/YYYY") }}<br />

            <small>{{ getDate(item.billingDate) | moment("from") }}</small>
          </td>
          <td class="text-right align-middle">
            {{ (item.amountDue / 100) | currency }}
          </td>
          <td class="text-right pr-0">
            <template v-if="!item.processedToLedger">
              <b-dropdown
                dropdown
                dropleft
                variant="link"
                v-if="hasRole('EDITPAYMENTPLANS')"
                toggle-class="text-decoration-none"
                class="p-0 m-0"
                no-caret
                size="lg"
                id="dropdown-1"
                text="Action"
              >
                <template #button-content
                  ><i class="fas fa-ellipsis-h"></i>
                </template>
                <b-dropdown-item @click.prevent="edit(item)">
                  <i class="fa-solid fa-pen"></i>Edit
                </b-dropdown-item>
                <b-dropdown-item
                  @click.prevent="
                    () => {
                      processPaymentNowAmount = (item.amountDue / 100).toFixed(
                        2
                      );
                      $refs.processPaymentNowModal.ask();
                    }
                  "
                  v-if="
                    firstWithoutLedger().id == item.id && item.amountDue > 0
                  "
                >
                  <i class="fa-solid fa-credit-card"></i>Process Payment
                </b-dropdown-item>
                <template
                  v-if="
                    myPaymentPlanDetails.length != index + 1 &&
                      item.amountDue > 0
                  "
                >
                  <b-dropdown-item
                    @click.prevent="ask('NEXT', item.id)"
                    v-if="item.amountDue > 0"
                  >
                    <i class="fa-solid fa-down"></i>Skip and Add to Next Cycle
                  </b-dropdown-item>

                  <b-dropdown-item
                    @click.prevent="ask('LAST', item.id)"
                    v-if="item.amountDue > 0"
                  >
                    <i class="fa-solid fa-down-to-line"></i>Skip and Add to Last
                    Cycle
                  </b-dropdown-item>

                  <b-dropdown-item
                    @click.prevent="ask('MOVEEND', item.id)"
                    v-if="item.amountDue > 0"
                  >
                    <i class="fa-solid fa-calendar-plus"></i>Skip and Create New
                    Cycle
                  </b-dropdown-item>
                </template>
              </b-dropdown>
            </template>
          </td>
        </tr>
      </template>
    </table>
  </div>
</template>

<script>
import Vue from "vue";

export default {
  name: "paymentplan-schedule-table",
  props: { paymentPlanDetails: null, title: null, paymentPlan: null },
  inject: ["hasRole"],
  data() {
    return {
      myPaymentPlanDetails: [],
      selectedItem: null,
      askFunc: null,
      processPaymentNowAmount: null,
    };
  },
  mounted() {
    this.myPaymentPlanDetails = JSON.parse(
      JSON.stringify(this.paymentPlanDetails)
    );
  },
  watch: {
    paymentPlanDetails: function(v) {
      this.myPaymentPlanDetails = v;
    },
  },
  methods: {
    async processPaymentNow() {
      let r = await this.$client.post("/api/paymentplan/processPaymentNow", {
        paymentPlanDetailId: this.firstWithoutLedger().id,
      });
      if (r.data.isSuccess) {
        this.$emit("rebind");
        this.$emitter.emit("success", {
          message: "The payment has been processed",
        });
      }
    },
    firstWithoutLedger() {
      let first = this.myPaymentPlanDetails.find(
        (item) => !item.processedToLedger
      );
      return first;
    },
    move() {
      this.movePayment(this.askFunc.action, this.askFunc.id);
    },
    ask(action, id) {
      this.askFunc = { action: action, id: id };
      this.$refs.confirmMove.ask();
    },
    async save() {
      let r = await this.$client.post("/api/paymentPlan/updateDetail", {
        ...this.selectedItem,
        paymentPlanId: this.paymentPlan.id,
      });

      if (r.data.isSuccess) {
        this.$emit("rebind");
        this.$emitter.emit("success", {
          message: "Payment plan has been updated",
        });
      }
    },

    edit(item) {
      this.myPaymentPlanDetails.forEach((e) => {
        e.isEdit = false;
        e.changeChildren = false;
      });
      item.isEdit = true;
      this.selectedItem = item;
      Vue.nextTick(() => {
        document.getElementById("amountInput" + item.cycle).focus();
        document.getElementById("amountInput" + item.cycle).select();
      });
    },
    async movePayment(action, id) {
      let r = await this.$client.post("/api/paymentPlan/moveDetail", {
        action: action,
        id: id,
      });
      if (r.data.isSuccess) {
        this.$emitter.emit("success", { message: "Payment plan updated" });
        this.$emit("rebind");
      }
    },
    getDate(d) {
      var d2 = new Date(d);
      var a = new Date();
      d2.setHours(a.getHours());
      d2.setMinutes(a.getMinutes());

      return d2;
    },
  },
};
</script>

<style>
.dropdown-menu .dropdown-item > i.fa-credit-card {
  font-size: 0.9em !important;
  position: relative;
  top: -1px;
}
</style>
